import { Link } from "react-router-dom";
import { Shield, Users, TrendingUp, BadgeCheck, ArrowRight, Bot, HandCoins, GraduationCap, Crown } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { TESTIMONIALS } from "@/data/testimonials";

const HERO_LOGO = "/brand/syndicate.png";
const HERO_BG_GRADIENT =
  "radial-gradient(ellipse at 50% 30%, rgba(70,70,70,0.35) 0%, rgba(10,10,10,0.95) 55%, #000 100%)";
const SOLANA_LOGO = "https://cryptologos.cc/logos/solana-sol-logo.svg?v=032";

const PILLARS = [
  {
    icon: TrendingUp,
    title: "MULTI-MARKET EDGE",
    desc: "Live trading ideas & analysis across crypto, stocks and forex — delivered by insiders trading real capital every session.",
  },
  {
    icon: Users,
    title: "TRADER NETWORK",
    desc: "A closed circle of market insiders — crypto, equities and FX operators sharing plays, order flow and setups you won't find publicly.",
  },
  {
    icon: BadgeCheck,
    title: "VERIFIED TESTIMONIALS",
    desc: "Real screenshots from real members. The insider edge shows up on the P&L — see the wins for yourself.",
  },
];

const BENEFITS = [
  {
    tag: "Benefit Access // 01",
    icon: Crown,
    iconType: "lucide",
    title: "Access to the Founder",
    desc: "Direct line to the founding partner — ask questions, share trades, and get personal guidance from someone deep in the markets every day.",
    img: "https://customer-assets-jai6qajn.emergentagent.net/job_community-redesign-1/artifacts/7515b4x8_38141241-A31C-4350-BEA5-F46119A55E13%202.jpg",
  },
  {
    tag: "Benefit Access // 02",
    icon: GraduationCap,
    iconType: "lucide",
    title: "Access to Mentors",
    desc: "Learn from seasoned mentors across crypto, stocks and forex. Weekly live rooms, breakdowns and one-to-one coaching to sharpen your edge.",
    img: "https://customer-assets-jai6qajn.emergentagent.net/job_community-redesign-1/artifacts/l4de34zi_IMG_5319.jpg",
  },
  {
    tag: "Benefit Access // 03",
    icon: HandCoins,
    iconType: "lucide",
    title: "Crypto Off-ramp & Spending",
    desc: "Turn on-chain gains into real-world spending. Trusted off-ramp partners, cards and payment rails so your profits move as fast as the market.",
    img: "https://images.unsplash.com/photo-1621761191319-c6fb62004040?q=80&w=1200&auto=format&fit=crop",
  },
  {
    tag: "Benefit Access // 04",
    icon: SOLANA_LOGO,
    iconType: "image",
    title: "SynDex",
    desc: "Our premium AI trading bot. SynDex scans markets 24/7 and delivers high-conviction trading ideas straight to members.",
    img: "solana-hero",
  },
];

const PARTNERS = [
  {
    src: "/brand/blacklane.png",
    name: "Blacklane",
    href: "https://www.blacklane.com/en/business/bd4b7db7b3246520ee1b/",
    scale: 1,
    invert: false,
  },
  {
    src: "/brand/entravel.png",
    name: "Entravel",
    href: "https://travel.thesyndicate.vip/?currency=GBP",
    scale: 1,
    invert: false,
  },
  {
    src: "/brand/mirai.png",
    name: "Mirai Flights",
    href: "https://mirai.flights/?ref=ac_a2e16435-75d7-4e95-8304-ead17980cda4&utm_source=agent_af13f2a2-9934-453d-a087-850b352c6f03",
    scale: 1.4,
    invert: false,
  },
  {
    src: "/brand/modo.png",
    name: "MODO Atelier",
    href: "https://modoatelier.com",
    scale: 0.72,
    invert: false,
  },
];;

export default function Home() {
  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden grain" data-testid="home-page">
      <Navbar />

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[92vh] flex flex-col items-center justify-center px-6 pt-32 pb-16"
      >
        <div
          className="absolute inset-0 opacity-90"
          style={{ background: HERO_BG_GRADIENT }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black" />
        <div className="absolute inset-0 dot-pattern opacity-40" />

        <div className="relative z-10 flex flex-col items-center text-center max-w-3xl">
          <img
            data-testid="hero-logo"
            src={HERO_LOGO}
            alt="The Syndicate"
            className="w-56 md:w-80 mx-auto fade-up delay-1 drop-shadow-[0_0_40px_rgba(255,255,255,0.15)]"
          />

          <div className="mt-10 md:mt-12 fade-up delay-2">
            <Link
              to="/pricing"
              data-testid="hero-join-now-btn"
              className="inline-flex items-center gap-3 btn-shimmer text-black font-mono-serif text-xs md:text-sm tracking-[0.3em] px-8 md:px-10 py-3.5 md:py-4 rounded-full hover:opacity-95 transition"
            >
              JOIN NOW
              <ArrowRight size={16} />
            </Link>
          </div>

          <p className="mt-8 font-mono-serif text-[10px] md:text-[11px] tracking-[0.4em] text-white/40 fade-up delay-3">
            CRYPTO  //  STOCKS  //  FOREX
          </p>
        </div>

        {/* Hero Video */}
        <div className="relative z-10 mt-12 md:mt-16 w-full max-w-4xl mx-auto fade-up delay-4">
          <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_0_60px_rgba(255,255,255,0.05)]">
            <video
              data-testid="hero-video"
              src="https://customer-assets-jai6qajn.emergentagent.net/job_community-redesign-1/artifacts/zhn66jph_copy_9B2A12F4-8929-4ABE-B619-10C8EC075BE5.MOV"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* MEMBERSHIP ENTRY */}
      <section className="relative z-10 px-4 md:px-6 -mt-6 md:mt-4">
        <div className="max-w-3xl mx-auto frame p-6 md:p-10">
          <p className="text-center font-mono-serif text-[10px] tracking-[0.4em] text-white/45">
            MEMBERSHIP  //  ENTRY
          </p>
          <div className="mt-5 flex items-center justify-center gap-3">
            <Shield size={22} className="text-white" />
            <h2 className="font-display text-3xl md:text-4xl tracking-tight">Join The Syndicate</h2>
          </div>
          <p className="mt-3 text-center text-white/60 text-sm md:text-base">
            Access our elite trading community of crypto, stocks and forex operators.
          </p>

          <div className="mt-8 flex flex-col gap-3">
            <Link
              to="/pricing"
              data-testid="membership-join-btn"
              className="btn-shimmer text-black font-mono-serif text-[11px] md:text-xs tracking-[0.3em] px-6 py-4 rounded-full text-center hover:opacity-95 transition"
            >
              JOIN THE SYNDICATE
            </Link>
            <a
              href="https://t.me/issyflashcalls"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="membership-free-btn"
              className="border border-white/15 text-white font-mono-serif text-[11px] md:text-xs tracking-[0.3em] px-6 py-4 rounded-full text-center hover:bg-white/5 transition"
            >
              JOIN FREE COMMUNITY
            </a>
            <Link
              to="/mentorship"
              data-testid="membership-mentorship-btn"
              className="border border-white/15 text-white font-mono-serif text-[11px] md:text-xs tracking-[0.3em] px-6 py-4 rounded-full text-center hover:bg-white/5 transition"
            >
              1-1 MENTORSHIP
            </Link>
          </div>
        </div>
      </section>

      {/* 3 PILLARS */}
      <section className="relative z-10 px-4 md:px-6 mt-14 md:mt-24">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {PILLARS.map((p, i) => (
            <div
              key={p.title}
              data-testid={`pillar-${i}`}
              className="frame p-6 md:p-8 flex flex-col items-center text-center"
            >
              <p.icon size={24} className="text-white/85" />
              <h3 className="mt-4 font-mono-serif text-xs tracking-[0.3em] text-white">
                {p.title}
              </h3>
              <p className="mt-4 text-white/60 text-sm md:text-[15px] leading-relaxed">
                {p.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* BRAND PARTNERS - Auto Carousel */}
      <section id="partners" className="relative z-10 px-4 md:px-6 mt-16 md:mt-24">
        <div className="max-w-5xl mx-auto frame p-8 md:p-12 overflow-hidden">
          <p className="text-center font-mono-serif text-[10px] tracking-[0.4em] text-white/45">
            BRAND PARTNERSHIPS
          </p>
          <div className="mt-8 overflow-hidden partners-fade">
            <div className="marquee-partners flex items-center gap-20 md:gap-28 w-max">
              {[...PARTNERS, ...PARTNERS, ...PARTNERS].map((p, idx) => (
                <a
                  key={`${p.name}-${idx}`}
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`partner-${p.name.toLowerCase().replace(/\s+/g, "-")}-${idx}`}
                  className="shrink-0 flex items-center justify-center h-20 md:h-28 w-52 md:w-72 opacity-70 hover:opacity-100 transition"
                  aria-label={p.name}
                >
                  <img
                    src={p.src}
                    alt={p.name}
                    className="max-h-full max-w-full object-contain"
                    style={{
                      transform: `scale(${p.scale || 1})`,
                      transformOrigin: "center",
                      filter: p.invert ? "invert(1)" : "none",
                    }}
                  />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MEMBER BENEFITS */}
      <section id="benefits" className="relative z-10 px-4 md:px-6 mt-20 md:mt-32">
        <div className="max-w-6xl mx-auto">
          <h2 className="mt-3 text-center font-display text-4xl md:text-6xl tracking-tight">
            Member Benefits
          </h2>
          <p className="mt-4 text-center text-white/55 max-w-2xl mx-auto">
            Real-world access. Real market edge. Every membership unlocks the tools and people that move markets.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {BENEFITS.map((b, i) => (
              <div
                key={b.title}
                data-testid={`benefit-${i}`}
                id={i === 3 ? "syndex" : undefined}
                className="benefit-card group"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  {b.img === "solana-hero" ? (
                    <div className="w-full h-full flex items-center justify-center" style={{ background: "radial-gradient(circle at 30% 30%, #9945FF, #14F195 90%, #050505 100%)" }}>
                      <img
                        src={SOLANA_LOGO}
                        alt="Solana"
                        className="w-2/3 h-2/3 object-contain drop-shadow-[0_0_30px_rgba(0,0,0,0.5)]"
                      />
                    </div>
                  ) : (
                    <img
                      src={b.img}
                      alt={b.title}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <div className="absolute top-4 left-5 font-mono-serif text-[10px] tracking-[0.3em] text-white/70">
                    {b.tag}
                  </div>
                  {b.iconType === "image" ? (
                    <img
                      src={b.icon}
                      alt="Solana"
                      className="absolute top-4 right-5 w-6 h-6 drop-shadow-[0_0_10px_rgba(153,69,255,0.6)]"
                    />
                  ) : (
                    <b.icon size={20} className="absolute top-4 right-5 text-white/80" />
                  )}
                </div>
                <div className="p-6 md:p-8 bg-black">
                  <h3 className="font-display text-2xl md:text-3xl">
                    {b.title}
                  </h3>
                  <p className="mt-3 text-white/60 text-sm md:text-[15px] leading-relaxed">
                    {b.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="relative z-10 mt-20 md:mt-32 scroll-mt-32">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <p className="text-center font-mono-serif text-[10px] tracking-[0.4em] text-white/45">
            TESTIMONIALS
          </p>
          <h2 className="mt-3 text-center font-display text-4xl md:text-5xl tracking-tight">
            What Members Say
          </h2>
        </div>
        <div className="mt-10 overflow-hidden">
          <div className="marquee flex items-center gap-6 w-max">
            {[...TESTIMONIALS, ...TESTIMONIALS].map((src, i) => (
              <img
                key={i}
                src={src}
                alt="Member testimonial"
                data-testid={`testimonial-${i}`}
                className="h-[420px] md:h-[500px] w-auto rounded-2xl border border-white/10 shrink-0 object-contain"
              />
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 px-4 md:px-6 mt-24 md:mt-32">
        <div className="max-w-3xl mx-auto text-center frame p-10 md:p-14">
          <h2 className="font-display text-4xl md:text-5xl">Ready to move with the market?</h2>
          <p className="mt-4 text-white/60 max-w-xl mx-auto">
            Claim your seat inside The Syndicate. Choose your market — crypto, stocks, or forex — and start trading with the pack.
          </p>
          <Link
            to="/pricing"
            data-testid="final-cta-join-btn"
            className="mt-8 inline-flex items-center gap-3 btn-shimmer text-black font-mono-serif text-xs tracking-[0.3em] px-8 py-4 rounded-full hover:opacity-95 transition"
          >
            JOIN NOW <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
